<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="showLoadingWidget" :status="initStatus" @init="init" />
        <EmptyPage
            v-else-if="isAllShiftFilled"
            title="Semua shift sudah terisi"
            description="Catatan kas hari ini sudah lengkap untuk semua shift."
            icon="fa fa-calendar-check-o"
            button-label="Kembali"
            button-to="/kasir/catatan-kas"
        />
        <b-card v-else>
            <template v-slot:header>
                <h5>Input Catatan Kas Harian</h5>
            </template>

            <b-form @submit.prevent="onSubmit">
                <div class="form-content-wrapper">
                    <b-form-group label="Staff yang melakukan Opname Kas">
                        <multiselect
                            v-model="input.staffOpname"
                            :options="staffOpnameOptions"
                            :internal-search="true"
                            :allow-empty="true"
                            deselect-label=""
                            select-label=""
                            label="name"
                            track-by="id"
                            placeholder="-- Pilih Staff --"
                            name="catatanKasStaffOpname"
                        />
                    </b-form-group>
                    <b-form-group v-if="isOtherStaffOpnameSelected()" label="Cari Staff Opname">
                        <multiselect
                            v-model="input.otherStaffOpname"
                            :options="searchedStaffOpnameOptions"
                            :internal-search="true"
                            :loading="isLoadingStaffOpname"
                            :allow-empty="false"
                            deselect-label=""
                            select-label=""
                            label="name"
                            track-by="id"
                            placeholder="-- Pilih Staff --"
                            name="catatanKasOtherStaffOpname"
                        />
                    </b-form-group>
                    <b-form-group label="Shift Aktif">
                        <b-form-select
                            v-model="shiftId"
                            :options="shiftOptions"
                            name="catatanKasShift"
                        >
                            <template v-slot:first>
                                <b-form-select-option :value="null" disabled>-- Pilih Shift --</b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-form-group>

                    <b-form-group label="Materai Tersedia">
                        <b-row>
                            <b-col md="4">
                                <b-input-group prepend="Jumlah">
                                    <b-form-input
                                        :value="input.materaiQty"
                                        name="catatanKasMaterai"
                                        inputmode="numeric"
                                        autocomplete="off"
                                        @keydown="onQtyKeydown($event)"
                                        @blur="onMateraiBlur"
                                        @input="onMateraiInput"
                                    />
                                    <b-input-group-append>
                                        <b-button variant="outline-secondary" @click="updateMateraiQtyByStep(-1)">-</b-button>
                                        <b-button variant="outline-secondary" @click="updateMateraiQtyByStep(1)">+</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>
                            <b-col md="4">
                                <div class="py-2">
                                    <!-- Rp. {{ $helper.addThousandSeparator(getMateraiNominal()) }} -->
                                </div>
                            </b-col>
                        </b-row>
                    </b-form-group>

                    <b-form-group label="Materai Terjual">
                        <b-row>
                            <b-col md="4">
                                <b-input-group prepend="Jumlah">
                                    <b-form-input
                                        :value="input.materaiTerjualQty"
                                        name="catatanKasMateraiTerjual"
                                        inputmode="numeric"
                                        autocomplete="off"
                                        @keydown="onQtyKeydown($event)"
                                        @blur="onMateraiTerjualBlur"
                                        @input="onMateraiTerjualInput"
                                    />
                                    <b-input-group-append>
                                        <b-button variant="outline-secondary" @click="updateMateraiTerjualQtyByStep(-1)">-</b-button>
                                        <b-button variant="outline-secondary" @click="updateMateraiTerjualQtyByStep(1)">+</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-col>
                            <b-col md="4">
                                <div class="py-2 small text-muted">
                                    Total: <strong class="text-dark">Rp. {{ $helper.addThousandSeparator(getMateraiTerjualNominal()) }}</strong>
                                </div>
                            </b-col>
                        </b-row>
                    </b-form-group>

                    <h6 class="form-title">Rincian Fisik Uang Kas Laci</h6>
                    <b-row>
                        <b-col
                            v-for="pecahan in denominations"
                            :key="pecahan.id"
                            md="4"
                        >
                            <b-form-group :label="`Rp. ${pecahan.label}`">
                                <b-row>
                                    <b-col cols="12" lg="7">
                                        <b-input-group prepend="Jumlah">
                                            <b-form-input
                                                :value="input.pecahanLaci[pecahan.id]"
                                                :name="`pecahan-laci-${pecahan.id}`"
                                                inputmode="numeric"
                                                autocomplete="off"
                                                @keydown="onQtyKeydown($event)"
                                                @blur="onQtyBlur('laci', pecahan.id)"
                                                @input="onPecahanInput('laci', pecahan.id, $event)"
                                            />
                                            <b-input-group-append>
                                                <b-button variant="outline-secondary" @click="updateQtyByStep('laci', pecahan.id, -1)">-</b-button>
                                                <b-button variant="outline-secondary" @click="updateQtyByStep('laci', pecahan.id, 1)">+</b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </b-col>
                                    <b-col cols="12" lg="5">
                                        <div class="py-2 small text-muted">
                                            Total: <strong class="text-dark">Rp. {{ $helper.addThousandSeparator(getPecahanNominal('laci', pecahan)) }}</strong>
                                        </div>
                                    </b-col>
                                </b-row>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <h6 class="form-title">Rincian Fisik Uang Kas Brankas</h6>
                    <b-row>
                        <b-col
                            v-for="pecahan in denominations"
                            :key="`brankas-${pecahan.id}`"
                            md="4"
                        >
                            <b-form-group :label="`Rp. ${pecahan.label}`">
                                <b-row>
                                    <b-col cols="12" lg="7">
                                        <b-input-group prepend="Jumlah">
                                            <b-form-input
                                                :value="input.pecahanBrankas[pecahan.id]"
                                                :name="`pecahan-brankas-${pecahan.id}`"
                                                inputmode="numeric"
                                                autocomplete="off"
                                                @keydown="onQtyKeydown($event)"
                                                @blur="onQtyBlur('brankas', pecahan.id)"
                                                @input="onPecahanInput('brankas', pecahan.id, $event)"
                                            />
                                            <b-input-group-append>
                                                <b-button variant="outline-secondary" @click="updateQtyByStep('brankas', pecahan.id, -1)">-</b-button>
                                                <b-button variant="outline-secondary" @click="updateQtyByStep('brankas', pecahan.id, 1)">+</b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </b-col>
                                    <b-col cols="12" lg="5">
                                        <div class="py-2 small text-muted">
                                            Total: <strong class="text-dark">Rp. {{ $helper.addThousandSeparator(getPecahanNominal('brankas', pecahan)) }}</strong>
                                        </div>
                                    </b-col>
                                </b-row>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <h6 class="form-title">Rincian Fisik Uang Kas Kecil</h6>
                    <b-row>
                        <b-col
                            v-for="pecahan in denominations"
                            :key="`kas-kecil-${pecahan.id}`"
                            md="4"
                        >
                            <b-form-group :label="`Rp. ${pecahan.label}`">
                                <b-row>
                                    <b-col cols="12" lg="7">
                                        <b-input-group prepend="Jumlah">
                                            <b-form-input
                                                :value="input.pecahanKasKecil[pecahan.id]"
                                                :name="`pecahan-kas-kecil-${pecahan.id}`"
                                                inputmode="numeric"
                                                autocomplete="off"
                                                @keydown="onQtyKeydown($event)"
                                                @blur="onQtyBlur('kasKecil', pecahan.id)"
                                                @input="onPecahanInput('kasKecil', pecahan.id, $event)"
                                            />
                                            <b-input-group-append>
                                                <b-button variant="outline-secondary" @click="updateQtyByStep('kasKecil', pecahan.id, -1)">-</b-button>
                                                <b-button variant="outline-secondary" @click="updateQtyByStep('kasKecil', pecahan.id, 1)">+</b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </b-col>
                                    <b-col cols="12" lg="5">
                                        <div class="py-2 small text-muted">
                                            Total: <strong class="text-dark">Rp. {{ $helper.addThousandSeparator(getPecahanNominal('kasKecil', pecahan)) }}</strong>
                                        </div>
                                    </b-col>
                                </b-row>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-form-group label="Catatan (Opsional)" class="mt-0">
                        <b-form-textarea
                            v-model="input.catatan"
                            name="catatanKasCatatan"
                            rows="3"
                            max-rows="6"
                            maxlength="100"
                            placeholder="Masukkan catatan"
                        />
                    </b-form-group>

                    <div class="mt-3">
                        <h6 class="form-title">Saldo Sistem</h6>
                        <div class="mb-2">
                            <strong>Kas Laci:</strong>
                            Rp. {{ $helper.addThousandSeparator(input.saldoKasLaci) }}
                        </div>
                        <div class="mb-2">
                            <strong>Kas Brankas:</strong>
                            Rp. {{ $helper.addThousandSeparator(input.saldoKasBrankas) }}
                        </div>
                        <div class="mb-2">
                            <strong>Total Kas:</strong>
                            Rp. {{ $helper.addThousandSeparator(getSaldoSistemTotal()) }}
                        </div>

                        <h6 class="form-title mt-4">Saldo Fisik</h6>
                        <div class="mb-2">
                            <strong>Kas Laci:</strong>
                            Rp. {{ $helper.addThousandSeparator(getKasLaci()) }}
                        </div>
                        <div class="mb-2">
                            <strong>Kas Brankas:</strong>
                            Rp. {{ $helper.addThousandSeparator(getKasBrankas()) }}
                        </div>
                        <div class="mb-2">
                            <strong>Total Kas:</strong>
                            Rp. {{ $helper.addThousandSeparator(getTotalKas()) }}
                        </div>

                        <h6 class="form-title mt-4">Inventaris</h6>
                        <div class="mb-1">
                            <strong>Materai Tersedia:</strong>
                            {{ parseNominal(input.materaiQty) }} lembar
                        </div>
                        <div class="mb-4">
                            <strong>Materai Terjual:</strong>
                            {{ parseNominal(input.materaiTerjualQty) }} lembar
                            (Nilai Rp. {{ $helper.addThousandSeparator(getMateraiTerjualNominal()) }})
                        </div>
                        <div class="mb-2">
                            <strong>Selisih Laci:</strong>
                            <span :class="getSelisihClassByValue(getSelisihInputLaci())">
                                Rp. {{ $helper.addThousandSeparator(Math.abs(getSelisihInputLaci())) }}
                            </span>
                        </div>
                        <div class="mb-2">
                            <strong>Selisih Brankas:</strong>
                            <span :class="getSelisihClassByValue(getSelisihInputBrankas())">
                                Rp. {{ $helper.addThousandSeparator(Math.abs(getSelisihInputBrankas())) }}
                            </span>
                        </div>
                        <div class="mb-2">
                            <strong>Total Selisih:</strong>
                            <span :class="getSelisihClassByValue(getTotalSelisihInput())">
                                Rp. {{ $helper.addThousandSeparator(Math.abs(getTotalSelisihInput())) }}
                            </span>
                        </div>

                        <h6 class="form-title mt-4">Rekonsiliasi Kas Kecil</h6>
                        <div class="mb-2">
                            <strong>Saldo Sistem:</strong>
                            Rp. {{ $helper.addThousandSeparator(input.saldoKasKecil) }}
                        </div>
                        <div class="mb-2">
                            <strong>Saldo Fisik:</strong>
                            Rp. {{ $helper.addThousandSeparator(getKasKecil()) }}
                        </div>
                        <div class="mb-2">
                            <strong>Selisih:</strong>
                            <span :class="getSelisihClassByValue(getSelisihInputKasKecil())">
                                Rp. {{ $helper.addThousandSeparator(Math.abs(getSelisihInputKasKecil())) }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="form-wrapper-btn submit-btn-wrapper d-flex flex-wrap justify-content-center">
                    <b-button
                        type="button"
                        variant="secondary"
                        class="mb-2 mb-md-0 mr-md-2 btn-mobile-action"
                        @click="$bvModal.show('modal-confirm-back-catatan-kas')"
                    >
                        Kembali
                    </b-button>
                    <b-button type="submit" variant="pgiBtn" name="catatanKasSubmitBtn" class="mb-2 mb-md-0 btn-mobile-action">
                        Hitung
                    </b-button>
                </div>
            </b-form>
        </b-card>

        <ModalConfirm
            id="modal-confirm-back-catatan-kas"
            title="Konfirmasi Kembali"
            desc="Apakah Anda yakin ingin kembali? Data yang belum disimpan akan hilang."
            @submit="goBack"
        />

        <StatementConfirmModal
            id="modal-confirm-finalize-catatan-kas"
            title="Confirm Submit Catatan Kas"
            :is-submitting="isSubmitting"
            @submit="submitFinalize"
        >
            <p>{{ finalizeConfirmDesc }}</p>
            <template v-slot:statement>
                <p class="mb-2">
                    Saya menyatakan bahwa seluruh data dan informasi yang saya input telah sesuai dengan kondisi sebenarnya.
                    Apabila ditemukan <strong>ketidaksesuaian</strong>, saya bersedia menerima
                    <strong>konsekuensi dan tindakan</strong> sesuai dengan ketentuan yang berlaku.
                </p>
            </template>
        </StatementConfirmModal>

        <b-modal
            id="modal-result-input-catatan-kas"
            title="Hasil Simpan Catatan Kas"
            size="lg"
            hide-footer
            centered
        >
            <div v-if="submitResult">
                <b-row>
                    <b-col md="6">
                        <div class="mb-2">
                            <strong>Tanggal:</strong>
                            {{ $helper.changeDateFormat(submitResult.createdAt || submitResult.tanggal) }}
                        </div>
                        <div class="mb-2"><strong>Shift:</strong> {{ getShiftLabel(submitResult.shift) }}</div>
                        <div class="mb-2"><strong>Dibuat Oleh:</strong> {{ submitResult.createdBy || "-" }}</div>
                        <div class="mb-2"><strong>Staff Opname:</strong> {{ submitResult.saksi || "-" }}</div>
                    </b-col>
                    <b-col md="6">
                        <div class="mb-3">
                            <h6 class="form-title mb-2 text-primary">
                                Saldo Sistem
                            </h6>
                            <div class="mb-1">
                                <strong>Kas Laci:</strong>
                                Rp. {{ $helper.addThousandSeparator(submitResult.saldoKasLaci || 0) }}
                            </div>
                            <div class="mb-1">
                                <strong>Kas Brankas:</strong>
                                Rp. {{ $helper.addThousandSeparator(submitResult.saldoKasBrankas || 0) }}
                            </div>
                            <div class="mb-1">
                                <strong>Total Kas:</strong>
                                Rp. {{ $helper.addThousandSeparator(getSubmitSaldoSistemTotal()) }}
                            </div>
                        </div>

                        <div class="mb-3">
                            <h6 class="form-title mb-2 mt-4 text-primary">
                                Saldo Fisik
                            </h6>
                            <div class="mb-1">
                                <strong>Kas Laci:</strong>
                                Rp. {{ $helper.addThousandSeparator(submitResult.totalInputLaci || 0) }}
                            </div>
                            <div class="mb-1">
                                <strong>Kas Brankas:</strong>
                                Rp. {{ $helper.addThousandSeparator(submitResult.totalInputBrankas || 0) }}
                            </div>
                            <div class="mb-1">
                                <strong>Total Kas:</strong>
                                Rp. {{ $helper.addThousandSeparator(getSubmitTotalKas()) }}
                            </div>
                        </div>
                        <div class="mb-3">
                            <h6 class="form-title mb-2 mt-4 text-primary">
                                Inventaris
                            </h6>
                            <div class="mb-1">
                                <strong>Materai Tersedia:</strong>
                                {{ getMateraiQtyFromNominal(submitResult.totalKasMaterai, submitResult.kelipatanMaterai) }} lembar
                            </div>
                            <div class="mb-1">
                                <strong>Materai Terjual:</strong>
                                {{ getMateraiQtyFromNominal(submitResult.totalKasMateraiTerjual, submitResult.kelipatanMaterai) }} lembar
                                (Nilai Rp. {{ $helper.addThousandSeparator(submitResult.totalKasMateraiTerjual || 0) }})
                            </div>
                        </div>
                        <div class="mb-2">
                            <strong>Selisih Laci:</strong>
                            <span :class="getSelisihClassByValue(getSelisihLaciValue())">
                                Rp. {{ $helper.addThousandSeparator(Math.abs(getSelisihLaciValue())) }}
                            </span>
                        </div>
                        <div class="mb-2">
                            <strong>Selisih Brankas:</strong>
                            <span :class="getSelisihClassByValue(getSelisihBrankasValue())">
                                Rp. {{ $helper.addThousandSeparator(Math.abs(getSelisihBrankasValue())) }}
                            </span>
                        </div>
                        <div class="mb-2">
                            <strong>Total Selisih:</strong>
                            <span :class="getSelisihClassByValue(getTotalSelisihValue())">
                                Rp. {{ $helper.addThousandSeparator(Math.abs(getTotalSelisihValue())) }}
                            </span>
                        </div>
                        <div class="mb-3">
                            <h6 class="form-title mb-2 text-primary">
                                Rekonsiliasi Kas Kecil
                            </h6>
                            <div class="mb-1">
                                <strong>Saldo Sistem:</strong>
                                Rp. {{ $helper.addThousandSeparator(submitResult.saldoKasKecil || 0) }}
                            </div>
                            <div class="mb-1">
                                <strong>Saldo Fisik:</strong>
                                Rp. {{ $helper.addThousandSeparator(submitResult.totalInputKasKecil || 0) }}
                            </div>
                            <div class="mb-1">
                                <strong>Selisih:</strong>
                                <span :class="getSelisihClassByValue(getSelisihKasKecilValue())">
                                    Rp. {{ $helper.addThousandSeparator(Math.abs(getSelisihKasKecilValue())) }}
                                </span>
                            </div>
                        </div>
                    </b-col>
                </b-row>

                <h6 class="form-title text-primary">Rincian Fisik Uang Kas Laci</h6>
                <b-table-simple small bordered responsive class="mb-3">
                    <b-thead>
                        <b-tr>
                            <b-th>Pecahan</b-th>
                            <b-th class="text-right">Jumlah</b-th>
                            <b-th class="text-right">Total</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="item in submitResult.detailsLaci || []" :key="`laci-${item.pecahanId}`">
                            <b-td>Rp. {{ item.pecahanLabel }}</b-td>
                            <b-td class="text-right">{{ item.qty || 0 }}</b-td>
                            <b-td class="text-right">Rp. {{ $helper.addThousandSeparator(item.totalNominal || 0) }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <h6 class="form-title text-primary">Rincian Fisik Uang Kas Brankas</h6>
                <b-table-simple small bordered responsive>
                    <b-thead>
                        <b-tr>
                            <b-th>Pecahan</b-th>
                            <b-th class="text-right">Jumlah</b-th>
                            <b-th class="text-right">Total</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="item in submitResult.detailsBrankas || []" :key="`brankas-${item.pecahanId}`">
                            <b-td>Rp. {{ item.pecahanLabel }}</b-td>
                            <b-td class="text-right">{{ item.qty || 0 }}</b-td>
                            <b-td class="text-right">Rp. {{ $helper.addThousandSeparator(item.totalNominal || 0) }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <h6 class="form-title text-primary">Rincian Fisik Uang Kas Kecil</h6>
                <b-table-simple small bordered responsive>
                    <b-thead>
                        <b-tr>
                            <b-th>Pecahan</b-th>
                            <b-th class="text-right">Jumlah</b-th>
                            <b-th class="text-right">Total</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="item in submitResult.detailsKasKecil || []" :key="`kas-kecil-${item.pecahanId}`">
                            <b-td>Rp. {{ item.pecahanLabel }}</b-td>
                            <b-td class="text-right">{{ item.qty || 0 }}</b-td>
                            <b-td class="text-right">Rp. {{ $helper.addThousandSeparator(item.totalNominal || 0) }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <b-alert :show="showKeteranganLaci" variant="warning" class="mb-3">
                    <strong>Keterangan Laci:</strong> {{ submitResult.keteranganLaci || "-" }}
                </b-alert>
                <b-alert :show="showKeteranganBrankas" variant="warning" class="mb-3">
                    <strong>Keterangan Brankas:</strong> {{ submitResult.keteranganBrankas || "-" }}
                </b-alert>
                <b-alert :show="showKeteranganKasKecil" variant="warning" class="mb-3">
                    <strong>Keterangan Kas Kecil:</strong> {{ submitResult.keteranganKasKecil || "-" }}
                </b-alert>

                <div class="mb-3 note-wrap">
                    <strong>Catatan:</strong> {{ submitResult.note || "-" }}
                </div>

                <div class="d-flex justify-content-end mt-3 gap-2">
                    <b-button variant="secondary" @click="$bvModal.hide('modal-result-input-catatan-kas')">
                        Tutup
                    </b-button>
                    <b-button
                        variant="pgiBtn"
                        :disabled="isSubmitting"
                        @click="openModalFinalizeConfirm"
                    >
                        Submit
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import EmptyPage from "@/components/common/EmptyPage";
import StatementConfirmModal from "@/components/common/StatementConfirmModal";
import { ENUM_PECAHAN_IDS, ENUM_SHIFT } from "@/constants/operasionalKas";

const DENOMINATIONS = Object.values(ENUM_PECAHAN_IDS)
    .sort((first, second) => first.id - second.id);
const OTHER_STAFF_OPNAME_OPTION = {
    id: "other",
    name: "Lainnya",
    isOther: true,
};

function buildPecahanData(denominations) {
    const pecahan = {};
    denominations.forEach((item) => {
        pecahan[item.id] = "0";
    });
    return pecahan;
}

export default {
    name: "input-catatan-kas",
    components: {
        EmptyPage,
        StatementConfirmModal,
    },
    data() {
        return {
            initStatus: 0,
            isAllShiftFilled: false,
            isSubmitting: false,
            submitResult: null,
            shiftId: null,
            shiftOptions: [],
            cabangId: this.$helper.getCabangId(),
            materaiNominal: 0,
            denominations: DENOMINATIONS,
            staffOpnameOptions: [],
            searchedStaffOpnameOptions: [],
            isLoadingStaffOpname: false,
            activeSearchStaffOpname: null,
            input: {
                staffOpname: null,
                otherStaffOpname: null,
                saldoKasProgram: 0,
                saldoKasLaci: 0,
                saldoKasBrankas: 0,
                saldoKasKecil: 0,
                materaiQty: "0",
                materaiTerjualQty: "0",
                catatan: "",
                pecahanLaci: buildPecahanData(DENOMINATIONS),
                pecahanBrankas: buildPecahanData(DENOMINATIONS),
                pecahanKasKecil: buildPecahanData(DENOMINATIONS),
            },
        };
    },
    methods: {
        init() {
            this.initStatus = 0;
            this.isAllShiftFilled = false;
            this.shiftId = null;
            this.shiftOptions = [];

            if (!this.$helper.hasPermissionMenuChildren('KKL')) {
                this.$helper.toastErr(this, "Anda tidak memiliki akses untuk input catatan kas.");
                this.initStatus = -1;
                return;
            }

            this.$axios.get(`cabang/${this.cabangId}`)
                .then((response) => {
                    const dataCabang = response.data;
                    this.input.saldoKasProgram = dataCabang.cashInHand || 0;
                    this.input.saldoKasLaci = Number(dataCabang.kasLaci || dataCabang.cashLaci || dataCabang.saldoLaci || 0);
                    this.input.saldoKasBrankas = Number(dataCabang.kasBrankas || dataCabang.cashBrankas || dataCabang.saldoBrankas || 0);

                    const staffs = Array.isArray(dataCabang.staffs) ? dataCabang.staffs : [];
                    this.staffOpnameOptions = [
                        ...this.normalizeStaffOptions(this.filterCurrentUserStaff(staffs)),
                        OTHER_STAFF_OPNAME_OPTION,
                    ];
                })
                .then(() => this.loadSaldoKasKecil())
                .then(() => this.loadMateraiNominal())
                .then(() => this.loadAvailableShiftOptions())
                .then(() => {
                    if (this.shiftOptions.length === 0) {
                        this.isAllShiftFilled = true;
                        this.initStatus = -1;
                        return;
                    }

                    this.initStatus = 1;
                })
                .catch((error) => {
                    console.log(error);
                    this.$helper.parseError(this, error);
                    this.initStatus = -1;
                });
        },
        loadSaldoKasKecil() {
            return this.$axios.post(`cabang/refresh-balance-kas-kecil?cabangId=${this.cabangId}`)
                .then((response) => {
                    this.input.saldoKasKecil = Number((response.data || {}).kasKecil || 0);
                });
        },
        loadAvailableShiftOptions() {
            return this.$axios.get(`log-book-kasir/available-shifts?cabangId=${this.cabangId}`)
                .then((response) => {
                    this.shiftOptions = this.normalizeShiftOptions(response.data);

                    if (!this.shiftOptions.some((item) => Number(item.value) === Number(this.shiftId))) {
                        this.shiftId = null;
                    }
                });
        },
        loadMateraiNominal() {
            return this.$axios.get("log-book-kasir/biaya-materai")
                .then((response) => {
                    const nominal = Number((response.data || {}).nominal);
                    if (!Number.isFinite(nominal) || nominal <= 0) {
                        throw new Error("Nominal materai tidak valid.");
                    }
                    this.materaiNominal = nominal;
                });
        },
        normalizeShiftOptions(data) {
            const shifts = Array.isArray(data)
                ? data
                : ((data || {}).shiftIds || (data || {}).shifts || (data || {}).data || []);

            return shifts
                .map((item) => ({
                    value: Number(item && typeof item === "object" ? (item.id || item.shift) : item),
                    text: item && typeof item === "object" ? (item.name || item.label) : this.getShiftLabel(item),
                }))
                .filter((item) => !Number.isNaN(item.value));
        },
        filterCurrentUserStaff(staffs) {
            const currentName = `${localStorage.getItem("name") || ""}`.trim().toLowerCase();
            if (!currentName) return staffs;

            return staffs.filter((staff) => {
                const staffNames = [
                    staff.name,
                    staff.fullname,
                    staff.fullName,
                    staff.username,
                    `${staff.username || ""} - ${staff.fullName || staff.fullname || staff.name || ""}`,
                ].map((name) => `${name || ""}`.trim().toLowerCase());

                return !staffNames.includes(currentName);
            });
        },
        isOtherStaffOpnameSelected() {
            return this.input.staffOpname && this.input.staffOpname.isOther === true;
        },
        getSelectedStaffOpname() {
            return this.isOtherStaffOpnameSelected()
                ? this.input.otherStaffOpname
                : this.input.staffOpname;
        },
        loadOtherStaffOpnameOptions() {
            this.isLoadingStaffOpname = true;
            this.searchedStaffOpnameOptions = [];

            this.$axios.get(`log-book-kasir/get-bawahan-kc?cabangId=${this.cabangId}`)
                .then((response) => {
                    const staffs = Array.isArray(response.data) ? response.data : [];
                    this.searchedStaffOpnameOptions = this.normalizeStaffOptions(this.filterCurrentUserStaff(staffs));
                })
                .catch((error) => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isLoadingStaffOpname = false;
                });
        },
        searchStaffOpname(searchQuery) {
            if (searchQuery.length >= 4) {
                this.isLoadingStaffOpname = true;

                if (this.activeSearchStaffOpname == null) {
                    this.activeSearchStaffOpname = setTimeout(() => { this.searchStaffOpnameFunc(searchQuery) }, 1000);
                } else {
                    clearTimeout(this.activeSearchStaffOpname);
                    this.activeSearchStaffOpname = setTimeout(() => { this.searchStaffOpnameFunc(searchQuery) }, 1000);
                }
            } else {
                clearTimeout(this.activeSearchStaffOpname);
                this.activeSearchStaffOpname = null;
                this.isLoadingStaffOpname = false;
            }
        },
        searchStaffOpnameFunc(searchQuery) {
            this.$axios.get(`user/search/?fullname=${searchQuery}`)
                .then((response) => {
                    const staffs = Array.isArray(response.data) ? response.data : [];
                    this.searchedStaffOpnameOptions = this.normalizeStaffOptions(this.filterCurrentUserStaff(staffs));
                })
                .catch((error) => {
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isLoadingStaffOpname = false;
                    this.activeSearchStaffOpname = null;
                });
        },
        normalizeStaffOptions(staffs) {
            return staffs.map((staff) => ({
                ...staff,
                name: this.formatStaffName(staff),
            }));
        },
        formatStaffName(staff) {
            const username = staff.username || "";
            const fullName = staff.fullName || staff.fullname || staff.name || "";
            if (username && fullName) return `${username} - ${fullName}`;
            return username || fullName;
        },
        getShiftLabel(shiftId) {
            if (Number(shiftId) === ENUM_SHIFT.PAGI.id) return "Pagi";
            if (Number(shiftId) === ENUM_SHIFT.SIANG.id) return "Siang";
            if (Number(shiftId) === ENUM_SHIFT.MALAM.id) return "Malam";
            return "-";
        },
        getTodayDate() {
            const date = new Date();
            const year = date.getFullYear();
            const month = `${date.getMonth() + 1}`.padStart(2, "0");
            const day = `${date.getDate()}`.padStart(2, "0");
            return `${year}-${month}-${day}`;
        },
        buildSubmitQuery(finalize = 0) {
            const pecahanIdsLaci = this.denominations.map((item) => item.id).join(",");
            const nominalsLaci = this.denominations
                .map((item) => this.getPecahanNominal("laci", item))
                .join(",");
            const pecahanIdsBrankas = this.denominations.map((item) => item.id).join(",");
            const nominalsBrankas = this.denominations
                .map((item) => this.getPecahanNominal("brankas", item))
                .join(",");
            const pecahanIdsKasKecil = this.denominations.map((item) => item.id).join(",");
            const nominalsKasKecil = this.denominations
                .map((item) => this.getPecahanNominal("kasKecil", item))
                .join(",");

            const params = {
                cabangId: `${this.cabangId}`,
                tanggal: this.getTodayDate(),
                shift: `${this.shiftId || ENUM_SHIFT.PAGI.id}`,
                saksiId: `${this.getSelectedStaffOpname().id}`,
                pecahanIdsLaci,
                nominalsLaci,
                pecahanIdsBrankas,
                nominalsBrankas,
                pecahanIdsKasKecil,
                nominalsKasKecil,
                nominalsMaterai: `${this.getMateraiNominal()}`,
                nominalsMateraiTerjual: `${this.getMateraiTerjualNominal()}`,
                finalize: `${finalize}`,
            };

            if (this.$helper.isNotNullAndNotEmpty(this.input.catatan)) {
                params.note = this.input.catatan;
            }

            return new URLSearchParams(params).toString();
        },
        isKeteranganNormal(keterangan = "") {
            const normalized = `${keterangan}`.trim().toUpperCase();
            return normalized === "OK" || normalized === "MATCH";
        },
        getPecahanTotal(scope) {
            return this.denominations.reduce((sum, item) => {
                const value = this.getPecahanNominal(scope, item);
                return sum + value;
            }, 0);
        },
        getPecahanNominal(scope, pecahan) {
            let source = this.input.pecahanLaci;
            if (scope === "brankas") source = this.input.pecahanBrankas;
            if (scope === "kasKecil") source = this.input.pecahanKasKecil;
            const qty = this.parseNominal(source[pecahan.id]);
            return qty * Number(pecahan.value || 0);
        },
        getMateraiNominal() {
            return this.parseNominal(this.input.materaiQty) * this.materaiNominal;
        },
        getMateraiTerjualNominal() {
            return this.parseNominal(this.input.materaiTerjualQty) * this.materaiNominal;
        },
        getMateraiQtyFromNominal(value, nominal = this.materaiNominal) {
            const materaiNominal = Number(nominal || 0);
            if (materaiNominal <= 0) return 0;
            return Number(value || 0) / materaiNominal;
        },
        getKasLaci() {
            return this.getPecahanTotal("laci");
        },
        getKasBrankas() {
            return this.getPecahanTotal("brankas");
        },
        getKasKecil() {
            return this.getPecahanTotal("kasKecil");
        },
        getTotalKas() {
            return this.getKasLaci() + this.getKasBrankas();
        },
        getSaldoSistemTotal() {
            const saldoLaci = Number(this.input.saldoKasLaci || 0);
            const saldoBrankas = Number(this.input.saldoKasBrankas || 0);
            return saldoLaci + saldoBrankas;
        },
        getSelisihInputLaci() {
            return Number(this.input.saldoKasLaci || 0) - Number(this.getKasLaci() || 0);
        },
        getSelisihInputBrankas() {
            return Number(this.input.saldoKasBrankas || 0) - Number(this.getKasBrankas() || 0);
        },
        getSelisihInputKasKecil() {
            return Number(this.input.saldoKasKecil || 0) - Number(this.getKasKecil() || 0);
        },
        getTotalSelisihInput() {
            return this.getSaldoSistemTotal() - this.getTotalKas();
        },
        getSelisihLaciValue() {
            if (!this.submitResult) return 0;
            const selisih = Number(this.submitResult.selisihLaci || 0);
            if (Number.isNaN(selisih)) return 0;
            return selisih;
        },
        getSelisihBrankasValue() {
            if (!this.submitResult) return 0;
            const selisih = Number(this.submitResult.selisihBrankas || 0);
            if (Number.isNaN(selisih)) return 0;
            return selisih;
        },
        getSelisihKasKecilValue() {
            if (!this.submitResult) return 0;
            const selisih = Number(this.submitResult.selisihKasKecil || 0);
            if (Number.isNaN(selisih)) return 0;
            return selisih;
        },
        getTotalSelisihValue() {
            if (!this.submitResult) return 0;
            return this.getSubmitSaldoSistemTotal() - this.getSubmitTotalKas();
        },
        getSubmitTotalKas() {
            if (!this.submitResult) return 0;
            return Number(this.submitResult.totalInputLaci || 0)
                + Number(this.submitResult.totalInputBrankas || 0);
        },
        getSubmitSaldoSistemTotal() {
            if (!this.submitResult) return 0;
            const saldoLaci = Number(this.submitResult.saldoKasLaci || 0);
            const saldoBrankas = Number(this.submitResult.saldoKasBrankas || 0);
            return saldoLaci + saldoBrankas;
        },
        getSelisihClassByValue(selisih) {
            if (Number(selisih) < 0) return "text-success";
            if (Number(selisih) > 0) return "text-danger";
            return "";
        },
        parseNominal(value) {
            if (value === null || value === undefined || value === "") return 0;
            const cleanValue = `${value}`.replace(/(?!^-)[^0-9]/g, "");
            if (cleanValue === "") return 0;
            return Number(cleanValue);
        },
        formatQty(value) {
            const cleanValue = `${value || ""}`.replace(/(?!^-)[^0-9]/g, "");
            if (cleanValue === "") return "";
            const normalizedValue = cleanValue.replace(/^0+(?=\d)/, "");
            if (normalizedValue === "") return "0";
            return normalizedValue;
        },
        normalizeInputValue(value) {
            if (value && typeof value === "object") {
                if (value.target && typeof value.target.value !== "undefined") {
                    return value.target.value;
                }
                if (typeof value.value !== "undefined") {
                    return value.value;
                }
                return "";
            }
            return value;
        },
        onQtyKeydown(event) {
            const allowedKeys = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End", "Enter"];
            if (allowedKeys.includes(event.key)) return;
            if (event.ctrlKey || event.metaKey) return;
            if (!/^\d$/.test(event.key)) event.preventDefault();
        },
        onQtyBlur(scope, denominationId) {
            let source = this.input.pecahanLaci;
            if (scope === "brankas") source = this.input.pecahanBrankas;
            if (scope === "kasKecil") source = this.input.pecahanKasKecil;
            const value = source[denominationId];
            if (value === null || value === undefined || value === "") {
                this.$set(source, denominationId, "0");
            }
        },
        onPecahanInput(scope, denominationId, value) {
            let source = this.input.pecahanLaci;
            if (scope === "brankas") source = this.input.pecahanBrankas;
            if (scope === "kasKecil") source = this.input.pecahanKasKecil;
            const rawValue = this.normalizeInputValue(value);
            const normalizedValue = this.formatQty(rawValue);
            this.$set(source, denominationId, normalizedValue);
        },
        updateQtyByStep(scope, denominationId, step) {
            let source = this.input.pecahanLaci;
            if (scope === "brankas") source = this.input.pecahanBrankas;
            if (scope === "kasKecil") source = this.input.pecahanKasKecil;
            const currentValue = this.parseNominal(source[denominationId]);
            const nextValue = Math.max(0, currentValue + Number(step || 0));
            this.$set(source, denominationId, `${nextValue}`);
        },
        onMateraiInput(value) {
            this.input.materaiQty = this.formatQty(this.normalizeInputValue(value));
        },
        onMateraiBlur() {
            if (this.input.materaiQty === null || this.input.materaiQty === undefined || this.input.materaiQty === "") {
                this.input.materaiQty = "0";
            }
        },
        updateMateraiQtyByStep(step) {
            const currentValue = this.parseNominal(this.input.materaiQty);
            this.input.materaiQty = `${Math.max(0, currentValue + Number(step || 0))}`;
        },
        onMateraiTerjualInput(value) {
            this.input.materaiTerjualQty = this.formatQty(this.normalizeInputValue(value));
        },
        onMateraiTerjualBlur() {
            if (this.input.materaiTerjualQty === null || this.input.materaiTerjualQty === undefined || this.input.materaiTerjualQty === "") {
                this.input.materaiTerjualQty = "0";
            }
        },
        updateMateraiTerjualQtyByStep(step) {
            const currentValue = this.parseNominal(this.input.materaiTerjualQty);
            this.input.materaiTerjualQty = `${Math.max(0, currentValue + Number(step || 0))}`;
        },
        onSubmit() {
            if (this.input.staffOpname == null) {
                this.$helper.toastErr(this, "Staff opname wajib dipilih.");
                return;
            }
            if (this.isOtherStaffOpnameSelected() && this.input.otherStaffOpname == null) {
                this.$helper.toastErr(this, "Staff opname lainnya wajib dipilih.");
                return;
            }
            if (this.shiftId == null || this.shiftId === "") {
                this.$helper.toastErr(this, "Shift wajib dipilih.");
                return;
            }
            this.isSubmitting = true;
            const queryParams = this.buildSubmitQuery(0);

            this.$axios.post(`log-book-kasir/?${queryParams}`)
                .then((response) => {
                    this.submitResult = response.data;
                    this.$bvModal.show("modal-result-input-catatan-kas");
                })
                .catch((error) => {
                    console.log(error);
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isSubmitting = false;
                });
        },
        submitFinalize() {
            this.isSubmitting = true;
            const queryParams = this.buildSubmitQuery(1);

            this.$axios.post(`log-book-kasir/?${queryParams}`)
                .then((response) => {
                    this.submitResult = response.data;
                    this.$helper.toastSucc(this, "Catatan kas berhasil di-submit.");
                    this.$bvModal.hide("modal-confirm-finalize-catatan-kas");
                    this.$bvModal.hide("modal-result-input-catatan-kas");
                    this.$router.push({ path: "/kasir/catatan-kas" });
                })
                .catch((error) => {
                    console.log(error);
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isSubmitting = false;
                });
        },
        openModalFinalizeConfirm() {
            this.$bvModal.show("modal-confirm-finalize-catatan-kas");
        },
        goBack() {
            const path = this.$route.path.split("/");
            path.pop();
            this.$router.push({ path: path.join("/") });
        },
    },
    computed: {
        showLoadingWidget() {
            return this.initStatus !== 1 && !this.isAllShiftFilled;
        },
        finalizeConfirmDesc() {
            const selisihLaci = this.getSelisihLaciValue();
            const selisihBrankas = this.getSelisihBrankasValue();
            const selisihKasKecil = this.getSelisihKasKecilValue();
            const warnings = [];

            if (selisihLaci < 0) warnings.push("selisih laci lebih");
            if (selisihLaci > 0) warnings.push("selisih laci kurang");
            if (selisihBrankas < 0) warnings.push("selisih brankas lebih");
            if (selisihBrankas > 0) warnings.push("selisih brankas kurang");
            if (selisihKasKecil < 0) warnings.push("selisih kas kecil lebih");
            if (selisihKasKecil > 0) warnings.push("selisih kas kecil kurang");

            if (warnings.length > 0) {
                return `Apakah anda yakin untuk submit final catatan kas harian ini? Data memiliki ${warnings.join(" dan ")}.`;
            }

            return "Apakah anda yakin untuk submit final catatan kas harian ini?";
        },
        showKeteranganLaci() {
            const value = this.submitResult ? this.submitResult.keteranganLaci : null;
            return this.$helper.isNotNullAndNotEmpty(value) && !this.isKeteranganNormal(value);
        },
        showKeteranganBrankas() {
            const value = this.submitResult ? this.submitResult.keteranganBrankas : null;
            return this.$helper.isNotNullAndNotEmpty(value) && !this.isKeteranganNormal(value);
        },
        showKeteranganKasKecil() {
            const value = this.submitResult ? this.submitResult.keteranganKasKecil : null;
            return this.$helper.isNotNullAndNotEmpty(value) && !this.isKeteranganNormal(value);
        },
    },
    watch: {
        "input.staffOpname"(value) {
            if (!value || value.isOther !== true) {
                this.input.otherStaffOpname = null;
                this.searchedStaffOpnameOptions = [];
                return;
            }

            this.input.otherStaffOpname = null;
            this.loadOtherStaffOpnameOptions();
        },
    },
    created() {
        this.init();
    },
};
</script>

<style scoped>
@media (max-width: 767.98px) {
    .btn-mobile-action {
        width: 100%;
    }
}

.note-wrap {
    overflow-wrap: anywhere;
    word-break: break-word;
}
</style>
